import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, ElementRef, HostListener, inject, input, signal } from '@angular/core';
import { GameService } from '@core/game/game.service';
import { LpCanvasComponentsComponent } from '@features/lp/lp-canvas/lp-canvas-components/lp-canvas-components.component';
import { LpCanvasElementPivotComponent } from '@features/lp/lp-canvas/lp-canvas-element-pivot/lp-canvas-element-pivot.component';
import { ILpCanvasRendererContext } from '@features/lp/lp-canvas/lp-canvas-renderer/canvas-contexts';
import { LpCanvasElementContext } from '@features/lp/lp-canvas/lp-canvas.types';
import { EASE_IN_BACK, EASE_OUT_BACK } from '@shared/css.utils';
import { workaroundPivot } from '@shared/workarounds.utils';
import { LpCanvasElement } from '@typings/lp/generated';

@Component({
  selector: 'lp-canvas-element',
  imports: [LpCanvasComponentsComponent, LpCanvasElementPivotComponent],
  templateUrl: './lp-canvas-element.component.html',
  styleUrl: './lp-canvas-element.component.css',
  animations: [
    trigger('visibility', [
      state('HIDDEN', style({ scale: 0 })),
      state('HIDDEN_INSTANT', style({ scale: 0 })),
      state('VISIBLE', style({ scale: 1 })),
      state('VISIBLE_INSTANT', style({ scale: 1 })),
      transition('* => VISIBLE', animate(`0.25s ${EASE_OUT_BACK}`)),
      transition('* => HIDDEN', animate(`0.25s ${EASE_IN_BACK}`)),
      transition('* => HIDDEN_INSTANT', animate(`0s`)),
      transition('* => VISIBLE_INSTANT', animate(`0s`)),
    ]),
  ],
})
export class LpCanvasElementComponent {
  readonly _gameService = inject(GameService);

  el = input.required<LpCanvasElement>();
  canvasCtx = input.required<ILpCanvasRendererContext>();
  element = inject<ElementRef<HTMLElement>>(ElementRef);
  context = new LpCanvasElementContext(this.canvasCtx, this.el, this.element);

  isHovering = signal(false);
  isActive = signal(false);
  pivot = computed(() => workaroundPivot(this.context.element().pivot));
  ngStyle = computed(() => ({
    borderRadius: this.el().shape.dataType === 'Circle' ? '50%' : '0%',
    ...this.context.style(),
    ...{ transition: this.context.transitionStyle() },
    ...(this.isHovering() ? this.context.hoverStyle() : {}),
    ...(this.isActive() ? this.context.activeStyle() : {}),
  }));

  @HostListener('mouseenter')
  onEnter() {
    this.isHovering.set(true);
  }

  @HostListener('mouseleave')
  onLeave() {
    this.isHovering.set(false);
    this.isActive.set(false);
  }

  @HostListener('mousedown')
  onMouseDown() {
    this.isActive.set(true);
  }

  @HostListener('mouseup')
  onMouseUp() {
    this.isActive.set(false);
  }
}
